import {
  Button,
  Modal,
  Segmented,
  Select,
  Space,
  Tag,
  Tooltip,
  message,
  theme,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiService } from "../../api";
import type { Task, TaskSummary } from "../../api/models/Task";
import type { UserList } from "../../api/models/UserList";
import { apiErrorMessage } from "../../api/apiError";
import { usePagination } from "../../hooks/usePagination";
import ManageableTable, {
  ManageableColumn,
} from "../../components/ManageableTable";
import { useRefreshOnTabFocus } from "../../tabs/tabContext";
import { refreshMyActionsCount } from "../../store/myActionsStore";
import { PageHeader, useHeaderSlot } from "../../components/PageHeader";
import { space } from "../../theme/space";
import { dueLabel, humanDuration } from "../../utils/duration";

/**
 * The task inbox: one list of what this person owes, across every process.
 *
 * What changed and why it needed a table to change it: "My actions" used to be
 * a list of *requests*, assembled at read time by unioning approval slots with
 * unsubmitted sections. That answers "what is on my plate?" and nothing else --
 * a derived row has no deadline to sort by, no age to show, and no way to be
 * claimed out of a team queue. Every column below except the case number is
 * something the old list could not have rendered at any price.
 *
 * Three decisions worth keeping:
 *
 * * **The server owns the order.** Rows arrive most-urgent-first (priority,
 *   then earliest deadline, undated work last) and the table does not re-sort
 *   them. Two orderings of one list is a page that disagrees with its own
 *   pagination.
 * * **A filter is a view, not a search.** The four views are the questions
 *   people actually open this page with -- everything, what is late, what is
 *   nearly late, and what nobody has taken. A general filter panel here would
 *   be answering a question nobody asked of an inbox.
 * * **A row shows the case AND the step.** "Permit Issuer on PTW-2024-114" is
 *   two facts and a person needs both; a list of case numbers makes you open
 *   each one to find out what it wants.
 */

type View = "all" | "overdue" | "due_soon" | "unclaimed" | "covering";

export const MyApprovalsPage = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [data, setData] = useState<Task[]>([]);
  const [summary, setSummary] = useState<TaskSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [view, setView] = useState<View>("all");
  const [reload, setReload] = useState(0);
  // The task being handed on, plus the pool it can be handed to. Held here
  // rather than per row so only one picker exists and only one search runs.
  const [handOn, setHandOn] = useState<Task | null>(null);
  const [candidates, setCandidates] = useState<UserList[]>([]);
  const [newOwner, setNewOwner] = useState<string | undefined>();
  const { page, pageSize, total, applyResponse, resetPage, paginationProps } =
    usePagination();
  const { slot, target } = useHeaderSlot();

  const load = useCallback(() => {
    setLoading(true);
    const params: Record<string, any> = { page, page_size: pageSize };
    if (view === "overdue") params.overdue = 1;
    if (view === "unclaimed") params.scope = "unclaimed";
    // Cover (D2): the borrowed rows on their own. A scope rather than a
    // client-side filter, or the page would page wrongly -- 20 rows fetched,
    // three of them somebody else's.
    if (view === "covering") params.scope = "covering";
    ApiService.apiMainTasksList(params)
      .then((result) => {
        let rows = applyResponse<Task>(result as any);
        // "Nearly late" is a read-time property (see Task.due_state on the
        // server), so it narrows the page rather than the query -- there is no
        // stored column saying "due soon" and inventing one would need a
        // scheduler to keep it true.
        if (view === "due_soon")
          rows = rows.filter((r) => r.due_state === "due_soon");
        setData(rows);
        refreshMyActionsCount();
      })
      .finally(() => setLoading(false));
    ApiService.apiMainTasksSummary()
      .then(setSummary)
      .catch(() => {
        /* A header count is never worth an error toast. */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, view, reload]);

  useEffect(load, [load]);

  // The point of this page is what still needs me: clearing something in a
  // request tab must drop it from here when I come back.
  useRefreshOnTabFocus(() => setReload((n) => n + 1));

  const act = async (task: Task, what: "claim" | "release") => {
    setBusyId(task.id);
    try {
      await (what === "claim"
        ? ApiService.apiMainTasksClaim(task.id)
        : ApiService.apiMainTasksRelease(task.id));
      message.success(
        what === "claim" ? t("Task claimed") : t("Task returned to the team")
      );
      setReload((n) => n + 1);
    } catch (error) {
      // Never `catch (e) { console.log(e) }` on an action: the server's refusal
      // is written in plain English and is the only thing that explains a
      // button doing nothing.
      message.error(apiErrorMessage(error, t("Could not update this task.")));
    } finally {
      setBusyId(null);
    }
  };

  const openHandOn = (task: Task) => {
    setHandOn(task);
    setNewOwner(undefined);
    setCandidates([]);
    // Restricted to the section's own pool, so the picker cannot offer somebody
    // the server is about to refuse. The same `group` filter the approver
    // picker uses for a role slot.
    ApiService.apiAccountsUsersList(undefined, task.group ?? undefined)
      .then(setCandidates)
      .catch(() => setCandidates([]));
  };

  const confirmHandOn = async () => {
    if (!handOn || !newOwner) return;
    setBusyId(handOn.id);
    try {
      await ApiService.apiMainTasksReassign(handOn.id, newOwner);
      message.success(t("Task handed on"));
      setHandOn(null);
      setReload((n) => n + 1);
    } catch (error) {
      message.error(apiErrorMessage(error, t("Could not update this task.")));
    } finally {
      setBusyId(null);
    }
  };

  const dueColour = (state: Task["due_state"]) =>
    state === "overdue"
      ? token.colorError
      : state === "due_soon"
      ? token.colorWarning
      : token.colorTextSecondary;

  const columns: ManageableColumn<Task>[] = [
    {
      key: "due",
      title: t("Due"),
      width: 150,
      hideable: false,
      render: (_, task) => {
        if (!task.due_at)
          return (
            <span style={{ color: token.colorTextQuaternary }}>{t("—")}</span>
          );
        return (
          <Tooltip title={new Date(task.due_at).toLocaleString()}>
            <span
              style={{
                color: dueColour(task.due_state),
                fontWeight: task.due_state === "overdue" ? 600 : 400,
              }}
            >
              {task.due_state === "overdue" ? "⚠ " : ""}
              {dueLabel(task.due_at, t)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: "what",
      title: t("What is needed"),
      width: 280,
      hideable: false,
      render: (_, task) => (
        <Space size={[6, 4]} wrap>
          <Tag color={task.kind === "Approval" ? "blue" : "orange"}>
            {task.kind === "Approval" ? t("Approve") : t("Fill")}
          </Tag>
          <span>{task.title}</span>
        </Space>
      ),
    },
    {
      key: "case",
      title: t("Case"),
      width: 200,
      hideable: false,
      render: (_, task) => (
        <Space direction="vertical" size={0}>
          <a href={`/requests/${task.request}`}>{task.request_number}</a>
          <span style={{ color: token.colorTextSecondary, fontSize: 12 }}>
            {task.template_short_name}
            {task.request_title ? ` · ${task.request_title}` : ""}
          </span>
        </Space>
      ),
    },
    {
      key: "step",
      title: t("Step"),
      width: 170,
      render: (_, task) => task.stage_name || t("—"),
    },
    {
      key: "owner",
      title: t("Owner"),
      width: 190,
      render: (_, task) =>
        task.assignee_name ? (
          <Space direction="vertical" size={0}>
            <span>{task.assignee_name}</span>
            {/* A borrowed row (D2). Said on the row rather than only in a
                filter, because the row is genuinely somebody else's: a
                stand-in who cannot tell whose work this is concludes the
                system has started assigning them other people's permits. */}
            {task.on_behalf_of && (
              <Tag color="gold" style={{ marginTop: 2 }}>
                {t("Covering for {{name}}", { name: task.on_behalf_of })}
              </Tag>
            )}
          </Space>
        ) : (
          // An unclaimed group task is a queue, not an orphan: everyone in the
          // pool sees it and one person takes it.
          <Tag color="purple">{task.group_name || t("Unassigned")}</Tag>
        ),
    },
    {
      key: "age",
      title: t("Waiting"),
      width: 110,
      render: (_, task) => humanDuration(task.age_seconds, t),
    },
    {
      key: "actions",
      title: t("Actions"),
      width: 160,
      hideable: false,
      render: (_, task) => (
        <Space size="small">
          <a href={`/requests/${task.request}`}>{t("Open")}</a>
          {task.can_claim && (
            <Button
              size="small"
              loading={busyId === task.id}
              onClick={() => act(task, "claim")}
            >
              {t("Claim")}
            </Button>
          )}
          {!task.can_claim && task.group && task.assignee && (
            <>
              <Button
                size="small"
                type="text"
                loading={busyId === task.id}
                onClick={() => act(task, "release")}
              >
                {t("Release")}
              </Button>
              {/* Only a team-owned section can be handed on: an approval slot
                  names the person whose authority is being used, and a
                  requester's own section moves with the case, not on its own. */}
              <Button size="small" type="text" onClick={() => openHandOn(task)}>
                {t("Hand on")}
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const meta = summary
    ? [
        t("{{count}} waiting on you", { count: summary.open }),
        summary.overdue ? t("{{count}} overdue", { count: summary.overdue }) : "",
        summary.unclaimed
          ? t("{{count}} unclaimed in your teams", { count: summary.unclaimed })
          : "",
        summary.covering
          ? t("{{count}} covering for colleagues", { count: summary.covering })
          : "",
      ]
        .filter(Boolean)
        .join(" · ")
    : t("{{count}} waiting on you", { count: total });

  return (
    <div>
      <PageHeader title={t("My Actions")} meta={meta} actions={slot} />
      <div style={{ marginBottom: space.base }}>
        <Segmented
          value={view}
          onChange={(value) => {
            setView(value as View);
            resetPage();
          }}
          options={[
            { label: t("Everything"), value: "all" },
            {
              label: summary?.overdue
                ? `${t("Overdue")} (${summary.overdue})`
                : t("Overdue"),
              value: "overdue",
            },
            { label: t("Due soon"), value: "due_soon" },
            {
              label: summary?.unclaimed
                ? `${t("Unclaimed")} (${summary.unclaimed})`
                : t("Unclaimed"),
              value: "unclaimed",
            },
            // Offered only when there is cover in force. A permanently
            // present tab reading 0 is a tab people stop seeing, and most
            // people are never standing in for anybody.
            ...(summary?.covering
              ? [
                  {
                    label: `${t("Covering")} (${summary.covering})`,
                    value: "covering",
                  },
                ]
              : []),
          ]}
        />
      </div>
      <Modal
        open={!!handOn}
        title={t("Hand this task to somebody else")}
        okText={t("Hand on")}
        cancelText={t("Cancel")}
        okButtonProps={{ disabled: !newOwner, loading: busyId === handOn?.id }}
        onOk={confirmHandOn}
        onCancel={() => setHandOn(null)}
      >
        <div style={{ marginBottom: space.tight, color: token.colorTextSecondary }}>
          {handOn?.title} · {handOn?.request_number}
        </div>
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: "100%" }}
          value={newOwner}
          onChange={setNewOwner}
          placeholder={t("Choose a member of {{group}}", {
            group: handOn?.group_name ?? "",
          })}
          options={candidates.map((u) => ({ value: u.id, label: u.name }))}
        />
      </Modal>
      <ManageableTable
        tableId="task-inbox"
        toolbarTarget={target}
        dataSource={data}
        columns={columns}
        loading={loading}
        pagination={paginationProps}
        rowKey={(record) => record.id}
      />
    </div>
  );
};

export default MyApprovalsPage;
